import React, { useEffect, useRef, useState } from "react";
import axios, { CancelToken, CancelTokenSource } from "axios";
import PQueue from "p-queue";

import { FILE_CHUNK_SIZE, MAX_REQUESTS_COUNT } from "@/helpers/constants";
import * as rax from "retry-axios";
import { formatToTimeZone } from "date-fns-timezone";
import UploadProgressBar, {
  UploadInfo,
} from "../UploadProgressBar/UploadProgressBar";
import { S3SignedRequestByIdResponse } from "@/components/common/HttpUpload/helpers";
import { getAPIBaseURL, SUBCONTRACTING_FOR_USER, TOKEN_KEY } from "@/env";

const API_COMPLETE_MULTIPART_UPLOAD = "/complete_multipart_upload";
const API_ABORT_MULTIPART_UPLOAD = "/abort_multipart_upload";

export interface InjectedHttpUploadProps {
  onFilesSelected: (files: File[]) => void;
  startUpload: (
    id: string,
    signedUrlFunction: (
      id: string,
      file: File,
      token: CancelToken,
    ) => Promise<S3SignedRequestByIdResponse>,
    filesToUpload?: File[],
    includeTime?: boolean,
  ) => Promise<string>;
  taskCompleted: () => void;
  isUploadStarted: boolean;
  isUploadCompleted: boolean;
  cancelUploadAlert: () => boolean;
}

interface HttpUploadProps {
  menuOpen: boolean;
  children: (props: InjectedHttpUploadProps) => React.ReactNode;
}

export default function HttpUpload({ menuOpen, children }: HttpUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadInfo, setUploadInfo] = useState<UploadInfo>({});
  const [uploadStartTime, setUploadStartTime] = useState<Date>();
  const [signedUrlCancelTokens, setSignedUrlCancelTokens] = useState<
    CancelTokenSource[]
  >([]);

  const filesQueue = useRef(
    new PQueue({ concurrency: MAX_REQUESTS_COUNT }),
  ).current;

  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission();
    }
    return () => {
      cancelOngoingUploads();
    };
  }, []);

  const isUploadStarted = () => Object.values(uploadInfo).length > 0;
  const isUploadCompleted = () =>
    Object.values(uploadInfo).every((v) => v.is_uploaded);

  const getWarningMessage = () =>
    !isUploadCompleted()
      ? "Your ongoing upload will be cancelled by this action."
      : "Changes you have made will be lost by this action.";

  const addBeforeUnloadListener = () => {
    window.onbeforeunload = (_: BeforeUnloadEvent) => getWarningMessage();
  };

  const removeBeforeUnloadListener = () => {
    window.onbeforeunload = null;
  };

  const cancelOngoingUploads = () => {
    filesQueue.clear();
    Object.values(uploadInfo).forEach((v) => {
      if (!v.is_uploaded && v.sources.length) {
        v.sources.forEach((source) => source.cancel("Cancelled"));
      }
    });
    signedUrlCancelTokens.forEach((token) => token.cancel("Cancelled"));
    setUploadStartTime(undefined);
    setUploadInfo({});
    setFiles([]);
    setSignedUrlCancelTokens([]);
    removeBeforeUnloadListener();
  };

  const handleUploadError = (error: Error) => {
    if (!isUploadStarted() || error.message === "Cancelled") return;
    cancelOngoingUploads();
    window.alert(
      "Something went wrong while uploading files. Please try again.",
    );
  };

  const cancelUploadAlert = () => {
    if (!isUploadStarted()) return true;
    const confirmCancel = window.confirm(getWarningMessage());
    if (confirmCancel) cancelOngoingUploads();
    return confirmCancel;
  };

  const onFilesSelected = (files: File[]) => setFiles(files);

  const startUpload = async (
    id: string,
    signedUrlFunction: (
      id: string,
      file: File,
      token: CancelToken,
    ) => Promise<S3SignedRequestByIdResponse>,
    filesToUpload?: File[],
    includeTime?: boolean,
  ): Promise<string> => {
    if (filesToUpload) setFiles(filesToUpload);

    return new Promise<string>((resolve, reject) => {
      addBeforeUnloadListener();

      const selectedFiles = filesToUpload || files;
      const newUploadInfo: UploadInfo = {};
      selectedFiles.forEach((file) => {
        newUploadInfo[file.name] = {
          is_uploaded: false,
          total: file.size,
          loaded: 0,
          sources: [],
        };
      });

      setUploadInfo(newUploadInfo);
      setUploadStartTime(new Date());

      const partsConcurrency =
        selectedFiles.length > MAX_REQUESTS_COUNT
          ? 1
          : MAX_REQUESTS_COUNT - selectedFiles.length + 1;

      const axiosInstance = axios.create();
      axiosInstance.defaults.raxConfig = {
        instance: axiosInstance,
        statusCodesToRetry: [
          [100, 199],
          [400, 429],
          [500, 599],
        ],
        retry: 3,
      };
      rax.attach(axiosInstance);

      const now = formatToTimeZone(new Date(), "YYYY_M_D_HH_mm_ss", {
        timeZone: "UTC",
      });

      const uploadPaths: string[] = [];
      const uploadPromises = selectedFiles.map((file) =>
        filesQueue.add(async () => {
          const cancelTokenSource = axios.CancelToken.source();
          setSignedUrlCancelTokens((prev) => [...prev, cancelTokenSource]);

          const signedUrls = await signedUrlFunction(
            id,
            includeTime ? new File([file], `${now}/${file.name}`) : file,
            cancelTokenSource.token,
          ).catch((err) => {
            handleUploadError(err);
            reject();
          });

          if (!signedUrls) return;

          const { urls, key, upload_id } = signedUrls;
          const partsQueue = new PQueue({ concurrency: partsConcurrency });
          const uploaded: { [index: number]: number } = {};
          const parts: { eTag: string; partNumber: number }[] = [];

          let uploadAborted = false;

          await Promise.all(
            urls.map((url, index) =>
              partsQueue.add(async () => {
                const start = index * FILE_CHUNK_SIZE;
                const end = (index + 1) * FILE_CHUNK_SIZE;
                const blob = file.slice(start, end);
                const tokenSource = axios.CancelToken.source();

                setUploadInfo((prev) => ({
                  ...prev,
                  [file.name]: {
                    ...prev[file.name],
                    sources: [...(prev[file.name]?.sources || []), tokenSource],
                  },
                }));

                const resp = await axiosInstance
                  .put(url, blob, {
                    headers: { "Content-Type": file.type },
                    withCredentials: true,
                    cancelToken: tokenSource.token,
                    onUploadProgress: ({ loaded }) => {
                      uploaded[index] = loaded;
                      setUploadInfo((prev) => ({
                        ...prev,
                        [file.name]: {
                          ...prev[file.name],
                          loaded: Object.values(uploaded).reduce(
                            (sum, val) => sum + val,
                            0,
                          ),
                        },
                      }));
                    },
                  })
                  .catch(async (err) => {
                    if (!uploadAborted) {
                      uploadAborted = true;
                      partsQueue.clear();
                      const token = localStorage.getItem(TOKEN_KEY);
                      await axios.post(
                        `${getAPIBaseURL()}${API_ABORT_MULTIPART_UPLOAD}`,
                        {
                          upload_id,
                          key,
                          graphene_id: id,
                        },
                        {
                          headers: {
                            "content-type": "application/x-www-form-urlencoded",
                            Authorization: `Bearer ${token}`,
                            "x-subcontracting-for-user": localStorage.getItem(
                              SUBCONTRACTING_FOR_USER,
                            ),
                          },
                        },
                      );
                      handleUploadError(err);
                      reject();
                    }
                  });

                if (resp) {
                  parts.push({
                    eTag: resp.headers.etag,
                    partNumber: index + 1,
                  });
                  if (parts.length === urls.length) {
                    const token = localStorage.getItem(TOKEN_KEY);
                    await axios
                      .post(
                        `${getAPIBaseURL()}${API_COMPLETE_MULTIPART_UPLOAD}`,
                        {
                          upload_id,
                          parts,
                          key,
                          graphene_id: id,
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                            "x-subcontracting-for-user": localStorage.getItem(
                              SUBCONTRACTING_FOR_USER,
                            ),
                          },
                        },
                      )
                      .then(() => {
                        setUploadInfo((prev) => ({
                          ...prev,
                          [file.name]: {
                            ...prev[file.name],
                            is_uploaded: true,
                          },
                        }));
                        uploadPaths.push(key.replace(`/${file.name}`, ""));
                      })
                      .catch((err) => {
                        handleUploadError(err);
                        reject();
                      });
                  }
                }
              }),
            ),
          );
        }),
      );

      // This waits for all uploads to finish....
      Promise.all(uploadPromises)
        .then(async () => {
          await filesQueue.onIdle();
          // All files uploaded right, so progress bar should be at 100%
          // Return the uploadPath of the last file or empty string if none
          const lastUploadPath =
            uploadPaths.length > 0 ? uploadPaths[uploadPaths.length - 1] : "";
          new Notification("Hotspring", {
            body: "Your upload is complete",
          });
          resolve(lastUploadPath);
        })
        .catch((err) => {
          handleUploadError(err);
          reject(err);
        });
    });
  };

  const onTaskCompleted = () => {
    removeBeforeUnloadListener();
  };

  const renderProgressBar = () => {
    if (files.length === 0) return null;
    return (
      <UploadProgressBar
        isUploadStarted={isUploadStarted()}
        uploadInfo={uploadInfo}
        uploadStartTime={uploadStartTime}
        onCancel={cancelUploadAlert}
        menuOpen={menuOpen}
      />
    );
  };

  return (
    <>
      {renderProgressBar()}
      {children({
        onFilesSelected,
        startUpload,
        taskCompleted: onTaskCompleted,
        isUploadStarted: isUploadStarted(),
        isUploadCompleted: isUploadCompleted(),
        cancelUploadAlert,
      })}
    </>
  );
}
